import React, { useState, useEffect } from 'react';
import { Container, Typography, Card, CardContent, Box,Checkbox, Grid, FormControl, InputLabel, Select, MenuItem, TextField, Button, CircularProgress, Alert, OutlinedInput, Chip } from '@mui/material';
import { api } from '../api';

const InsightsPage = ({ state }) => {
    const [filters, setFilters] = useState({
        platforms: [],
        sentiments: [],
        startDate: '',
        endDate: ''
    });
    const [isLoading, setIsLoading] = useState(false);
    const [insights, setInsights] = useState(null);
    const [error, setError] = useState('');

    const handleFilterChange = (event) => {
        const { name, value } = event.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleGenerateInsights = async () => {
        setIsLoading(true);
        setError('');
        setInsights(null);

        // Filter the mentions based on the UI controls
        let data = state.mentions;
        if (filters.platforms.length > 0) data = data.filter(m => filters.platforms.includes(m.platform));
        if (filters.sentiments.length > 0) data = data.filter(m => filters.sentiments.includes(m.sentiment));
        if (filters.startDate) data = data.filter(m => new Date(m.publishedAt) >= new Date(filters.startDate));
        if (filters.endDate) data = data.filter(m => new Date(m.publishedAt) <= new Date(filters.endDate));

        if (data.length === 0) {
            setError('No mentions match the selected filters. Please broaden your criteria.');
            setIsLoading(false);
            return;
        }

        try {
            const response = await api.post('/insights/generate', { mentions: data });
            setInsights(response.data);
        } catch (err) {
            setError('Failed to generate insights. The AI model may be temporarily unavailable.');
        } finally {
            setIsLoading(false);
        }
    };
    
    const platformOptions = [...new Set(state.mentions.map(m => m.platform))];
    const sentimentOptions = ['Positive', 'Negative', 'Neutral'];

    return (
        <Container maxWidth="lg">
            <Typography variant="h4" gutterBottom>Strategic Insights Generator</Typography>
            
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>Filter Data for Analysis</Typography>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth>
                                <InputLabel>Platforms</InputLabel>
                                <Select multiple name="platforms" value={filters.platforms} onChange={handleFilterChange} input={<OutlinedInput label="Platforms" />} renderValue={(selected) => selected.join(', ')}>
                                    {platformOptions.map(p => <MenuItem key={p} value={p}><Checkbox checked={filters.platforms.indexOf(p) > -1} />{p}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                             <FormControl fullWidth>
                                <InputLabel>Sentiments</InputLabel>
                                <Select multiple name="sentiments" value={filters.sentiments} onChange={handleFilterChange} input={<OutlinedInput label="Sentiments" />} renderValue={(selected) => selected.join(', ')}>
                                    {sentimentOptions.map(s => <MenuItem key={s} value={s}><Checkbox checked={filters.sentiments.indexOf(s) > -1} />{s}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}><TextField name="startDate" value={filters.startDate} onChange={handleFilterChange} type="date" label="Start Date" InputLabelProps={{ shrink: true }} fullWidth /></Grid>
                        <Grid item xs={12} md={3}><TextField name="endDate" value={filters.endDate} onChange={handleFilterChange} type="date" label="End Date" InputLabelProps={{ shrink: true }} fullWidth /></Grid>
                    </Grid>
                    <Button variant="contained" onClick={handleGenerateInsights} disabled={isLoading} sx={{ mt: 2 }}>
                        {isLoading ? 'Generating Insights...' : 'Generate Insights'}
                    </Button>
                </CardContent>
            </Card>
            
            {isLoading && <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><CircularProgress /></Box>}
            {error && <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>}

            {insights && (
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <Typography variant="h5" gutterBottom>Strategic Insights</Typography>
                                <Box component="ul" sx={{ pl: 2 }}>
                                    {insights.strategicInsights.map((insight, index) => (
                                        <Typography component="li" key={index} sx={{ mb: 1 }}>{insight}</Typography>
                                    ))}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <Typography variant="h5" gutterBottom>{insights.marketingCampaign.title}</Typography>
                                <Chip label={insights.marketingCampaign.tagline} color="primary" sx={{ mb: 2 }} />
                                <Typography variant="body1">{insights.marketingCampaign.description}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    {insights.marketingCampaign.images.map((img, index) => (
                         <Grid item xs={12} md={6} key={index}>
                            <Card>
                                <CardContent>
                                     <Typography variant="h6" gutterBottom>Campaign Graphic {index + 1}</Typography>
                                     <img src={img} alt={`Campaign graphic ${index + 1}`} style={{ width: '100%', borderRadius: '8px' }} />
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}
        </Container>
    );
};

export default InsightsPage;
